import { useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import getLabel from "helpers/getLabel";
import Button from "components/atoms/Button/Button";
import FormField from "components/molecules/FormField/FormField";
import { ReactComponent as EditPencil } from "assets/images/icons/EditPencil.svg";
import DatePicker from "components/atoms/DatePicker/DatePicker";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import BillingInfoDialog from "./BillingInfoDialog";

function InvoiceFormGeneral(props: any) {
  const { invoiceDraftData, invoiceConfig, invoices } = props;
  const [isEditBilling, setIsEditBilling] = useState(false);

  const intl = useIntl();
  const methods = useFormContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invoiceId = searchParams.get("invoice");
  const { setValue, watch } = methods;
  const { id } = useParams();

  const currentInvoice = invoices.find((inv: any) => inv.invoice_id === invoiceId);
  const isCustomsBrokerInvoice = currentInvoice?.invoice_type === "customs_broker_invoice";

  const { organization } = invoiceDraftData || {};
  const {
    integration_config,
    provider_invoice_ref_01,
    provider_invoice_ref_02,
    provider_invoice_ref_03,
    provider_invoice_ref_04,
    bol_reference_enabled,
    enable_new_address_structure,
  } = invoiceConfig || {};
  const { booking_reference, shipment_title, financial } = (invoices && invoices[0]) || {};

  function formatEntities(entities: string[]) {
    return entities.map((entity) => ({ label: entity, value: entity }));
  }

  const handleNavigateShipment = () => {
    navigate(`/shipment/${id}`);
  };

  return (
    <>
      <BillingInfoDialog
        isOpen={isEditBilling}
        close={() => setIsEditBilling(false)}
        isAddressV2Enabled={enable_new_address_structure}
      />
      <Row className="p-5">
        <Col>
          <Row className="mt-2">
            <Col className="font-b9">
              <FormattedMessage id="billing.shipmentNumber" defaultMessage="Shipment number" />
            </Col>
            <Col>
              <Button variant="link" style={{ padding: 0, border: 0 }} onClick={handleNavigateShipment}>
                {id}
              </Button>
            </Col>
          </Row>
          {shipment_title && (
            <Row className="mt-2">
              <Col className="font-b9">{getLabel("Shipment title", "capitalize-first")}</Col>
              <Col>{shipment_title}</Col>
            </Row>
          )}
          {!isCustomsBrokerInvoice && financial.rate_card_title && (
            <Row className="mt-2">
              <Col className="font-b9">Rate card</Col>
              <Col>{financial.rate_card_title}</Col>
            </Row>
          )}
          {organization?.cost_center && (
            <Row className="mt-2">
              <Col className="font-b9">{getLabel("Cost Center / WBS / Project #", "capitalize")}</Col>
              <Col>{organization.cost_center}</Col>
            </Row>
          )}
          {organization?.custom_field_01 && (
            <Row className="mt-2">
              <Col className="font-b9">{getLabel("Custom field 01", "capitalize")}</Col>
              <Col>{organization?.custom_field_01}</Col>
            </Row>
          )}
          {organization?.custom_field_02 && (
            <Row className="mt-2">
              <Col className="font-b9">{getLabel("Custom field 02", "capitalize")}</Col>
              <Col>{organization?.custom_field_02}</Col>
            </Row>
          )}
          {organization?.custom_field_03 && (
            <Row className="mt-2">
              <Col className="font-b9">{getLabel("Custom field 03", "capitalize")}</Col>
              <Col>{organization?.custom_field_03}</Col>
            </Row>
          )}
          {booking_reference && (
            <Row className="mt-2">
              <Col className="font-b9">{getLabel("Booking reference", "capitalize")}</Col>
              <Col>{booking_reference}</Col>
            </Row>
          )}
          {organization?.trackings?.tracking_number_0 && (
            <Row className="mt-2">
              <Col className="font-b9">{getLabel("Tracking number", "capitalize")}</Col>
              <Col>{organization.trackings.tracking_number_0}</Col>
            </Row>
          )}
          {organization?.trackings?.tracking_number_1 && (
            <Row className="mt-2">
              <Col className="font-b9">{getLabel("Tracking number 1", "capitalize")}</Col>
              <Col> {organization.trackings.tracking_number_1}</Col>
            </Row>
          )}
          {organization?.trackings?.tracking_number_2 && (
            <Row className="mt-2">
              <Col className="font-b9">{getLabel("Tracking number 2", "capitalize")}</Col>
              <Col>{organization.trackings.tracking_number_2}</Col>
            </Row>
          )}
          {organization?.trackings?.tracking_number_3 && (
            <Row className="mt-2">
              <Col className="font-b9">{getLabel("Tracking number 3", "capitalize")}</Col>
              <Col>{organization.trackings.tracking_number_3} </Col>
            </Row>
          )}
          {organization?.trackings?.tracking_number_4 && (
            <Row className="mt-2">
              <Col className="font-b9">{getLabel("Tracking number 4", "capitalize")}</Col>
              <Col>{organization.trackings.tracking_number_4} </Col>
            </Row>
          )}
          {organization?.trackings?.tracking_number_5 && (
            <Row className="mt-2">
              <Col className="font-b9">{getLabel("Tracking number 5", "capitalize")}</Col>
              <Col>{organization.trackings.tracking_number_5} </Col>
            </Row>
          )}
          {organization?.trackings?.tracking_number_6 && (
            <Row className="mt-2">
              <Col className="font-b9">{getLabel("Tracking number 6", "capitalize")}</Col>
              <Col>{organization.trackings.tracking_number_6} </Col>
            </Row>
          )}
          {organization?.trackings?.tracking_number_7 && (
            <Row className="mt-2">
              <Col className="font-b9">{getLabel("Tracking number 7", "capitalize")}</Col>
              <Col>{organization.trackings.tracking_number_7} </Col>
            </Row>
          )}
        </Col>
        {/* Invoice form */}
        <Col className="d-flex justify-content-center">
          <div>
            <div className="w-192">
              <FormField
                label={intl.formatMessage({
                  id: "billing.invoiceNumber",
                  defaultMessage: "Invoice number",
                })}
                type="textinput"
                placeholder={intl.formatMessage({
                  id: "general.typeHere",
                  defaultMessage: "Type here..",
                })}
                field_id="invoice.invoice_no"
                mandatory
                onChange={(e) => {
                  setValue("invoice.invoice_no", e.target.value);
                }}
              />
            </div>
            {integration_config?.length > 0 ? (
              <>
                {provider_invoice_ref_01 !== 0 && (
                  <div className="mt-3">
                    <label className={provider_invoice_ref_01 === 1 ? "required" : ""}>
                      {getLabel("Provider invoice reference 01", "capitalize")}
                    </label>
                    <Dropdown
                      useHookForm
                      name="invoice.provider_invoice_reference_01"
                      required={provider_invoice_ref_01 === 1}
                      options={formatEntities(integration_config[0].lsp_entities)}
                      defaultValue={formatEntities(integration_config[0]?.lsp_entities)[0]?.value}
                    />
                  </div>
                )}
                {provider_invoice_ref_02 !== 0 && (
                  <div className="mt-3">
                    {integration_config[0]?.company_name === "maxeon-solar-technologies" ? (
                      <div>
                        <label>{getLabel("Provider invoice reference 02", "capitalize")}</label>
                        <DatePicker wrapperClassNames="w-192" name="invoice.provider_invoice_reference_02" />
                      </div>
                    ) : (
                      <FormField
                        label={getLabel("Provider invoice reference 02", "capitalize")}
                        type="textinput"
                        placeholder={intl.formatMessage({
                          id: "general.typeHere",
                          defaultMessage: "Type here..",
                        })}
                        field_id="invoice.provider_invoice_reference_01"
                        mandatory
                        onChange={(e) => {
                          setValue("invoice.provider_invoice_reference_01", e.target.value);
                        }}
                      />
                    )}
                  </div>
                )}
                {provider_invoice_ref_03 !== 0 && (
                  <div className="mt-3">
                    <label className={provider_invoice_ref_03 === 1 ? "required" : ""}>
                      {getLabel("Provider invoice reference 03", "capitalize")}
                    </label>
                    <Dropdown
                      useHookForm
                      name="invoice.provider_invoice_reference_03"
                      required={provider_invoice_ref_03 === 1}
                      options={formatEntities(integration_config[0].currencies)}
                      defaultValue={formatEntities(integration_config[0]?.currencies)[0]?.value}
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                {bol_reference_enabled !== 0 && (
                  <div className="w-192 mt-3">
                    <FormField
                      label={intl.formatMessage({
                        id: "invoices.blReference",
                        defaultMessage: "B/L reference",
                      })}
                      type="textinput"
                      placeholder={intl.formatMessage({
                        id: "general.typeHere",
                        defaultMessage: "Type here..",
                      })}
                      field_id="invoice.bl_reference"
                      mandatory={bol_reference_enabled === 1}
                      onChange={(e) => {
                        setValue("invoice.bl_reference", e.target.value);
                      }}
                    />
                  </div>
                )}
                {provider_invoice_ref_01 !== 0 && (
                  <div className="w-192 mt-3">
                    <FormField
                      label={getLabel("Provider invoice reference 01", "capitalize")}
                      type="textinput"
                      placeholder={intl.formatMessage({
                        id: "general.typeHere",
                        defaultMessage: "Type here..",
                      })}
                      field_id="invoice.provider_invoice_reference_01"
                      mandatory={provider_invoice_ref_01 === 1}
                      onChange={(e) => {
                        setValue("invoice.provider_invoice_reference_01", e.target.value);
                      }}
                    />
                  </div>
                )}
                {provider_invoice_ref_02 !== 0 && (
                  <div className="w-192 mt-3">
                    <FormField
                      label={getLabel("Provider invoice reference 02", "capitalize")}
                      type="textinput"
                      placeholder={intl.formatMessage({
                        id: "general.typeHere",
                        defaultMessage: "Type here..",
                      })}
                      field_id="invoice.provider_invoice_reference_02"
                      mandatory={provider_invoice_ref_02 === 1}
                      onChange={(e) => {
                        setValue("invoice.provider_invoice_reference_02", e.target.value);
                      }}
                    />
                  </div>
                )}
                {provider_invoice_ref_03 !== 0 && (
                  <div className="w-192 mt-3">
                    <FormField
                      label={getLabel("Provider invoice reference 03", "capitalize")}
                      type="textinput"
                      placeholder={intl.formatMessage({
                        id: "general.typeHere",
                        defaultMessage: "Type here..",
                      })}
                      field_id="invoice.provider_invoice_reference_03"
                      mandatory={provider_invoice_ref_03 === 1}
                      onChange={(e) => {
                        setValue("invoice.provider_invoice_reference_03", e.target.value);
                      }}
                    />
                  </div>
                )}
                {provider_invoice_ref_04 !== 0 && (
                  <div className="w-192 mt-3">
                    <FormField
                      label={getLabel("Provider invoice reference 04", "capitalize")}
                      type="textinput"
                      placeholder={intl.formatMessage({
                        id: "general.typeHere",
                        defaultMessage: "Type here..",
                      })}
                      field_id="invoice.provider_invoice_reference_04"
                      mandatory={provider_invoice_ref_04 === 1}
                      onChange={(e) => {
                        setValue("invoice.provider_invoice_reference_04", e.target.value);
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </Col>
        {/* Billed to-from */}
        <Col>
          <div>
            <div className="font-b9 mb-1">
              <FormattedMessage id="billing.billedFrom" defaultMessage="Billed from" />:
              <EditPencil className="ml-2 cursor-pointer" onClick={() => setIsEditBilling(true)} />
            </div>
            <div>{watch("invoice.billed_from.company")}</div>
            <div>{watch("invoice.billed_from.address")}</div>
            <div>
              {watch("invoice.billed_from.city")}, {watch("invoice.billed_from.country")}
            </div>
          </div>
          <div className="mt-5">
            <div className="font-b9 mb-1">
              <FormattedMessage id="billing.billedTo" defaultMessage="Billed to:" />
              <EditPencil className="ml-2 cursor-pointer" onClick={() => setIsEditBilling(true)} />
            </div>
            <div>{watch("invoice.billed_to.company")}</div>
            <div>{watch("invoice.billed_to.address")}</div>
            <div>
              {watch("invoice.billed_to.city")}, {watch("invoice.billed_to.country")}
            </div>
          </div>
        </Col>
      </Row>
    </>
  );
}

export default InvoiceFormGeneral;
